import { ApiResponse, PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  CommunityResponse,
  CommunityListQueryParams,
  UpdateStatusPayload,
  ICommunityData,
  ICommunityTranslation,
  PaginatedRequestResponse,
  SingleCommunity,
  IEventData,
  IEventQueryParams,
} from "@/types/community-management.types";

export const CommunityManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCommunities: builder.query<
      PaginatedApiResponse<CommunityResponse[]>,
      CommunityListQueryParams
    >({
      query: (params) => ({
        url: "communities",
        method: "GET",
        params: {
          ...params,
        },
      }),
      transformResponse: (
        response: PaginatedApiResponse<CommunityResponse[]>,
      ) => response,
      providesTags: ["Communities"],
      keepUnusedDataFor: 0,
    }),

    getCommunityById: builder.query<SingleCommunity, string>({
      query: (community_id) => ({
        url: `communities/${community_id}`,
        method: "GET",
      }),
      transformResponse: (response: SingleCommunity) => response,
      providesTags: (result, error, community_id) => [
        { type: "Communities", id: community_id },
      ],
      keepUnusedDataFor: 0,
    }),

    createCommunity: builder.mutation<
      ApiResponse<void>,
      Partial<ICommunityData>
    >({
      query: (data) => ({
        url: "communities",
        method: "POST",
        data,
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Communities"],
    }),

    updateCommunity: builder.mutation<
      ApiResponse<void>,
      { community_id: string; data: Partial<ICommunityData> }
    >({
      query: ({ community_id, data }) => ({
        url: `communities/${community_id}`,
        method: "PATCH",
        data,
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Communities"],
    }),

    deleteCommunity: builder.mutation<ApiResponse<void>, string>({
      query: (community_id) => ({
        url: `communities/${community_id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Communities"],
    }),

    updateCommunityStatus: builder.mutation<
      ApiResponse<void>,
      UpdateStatusPayload
    >({
      query: ({ communityId, status }) => ({
        url: "communities/status",
        method: "PATCH",
        data: { communityId, status },
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Communities"],
    }),

    getCommunityRequests: builder.query<
      PaginatedRequestResponse,
      {
        page?: number;
        limit?: number;
        status?: string;
        search?: string;
        community_id: string; // Make community_id required
      }
    >({
      query: ({ community_id, ...params }) => ({
        url: `communities/${community_id}/requests`,
        method: "GET",
        params: {
          page: params.page,
          limit: params.limit,
          status: params.status,
          search: params.search,
          community_id, // Include community_id as a query parameter
        },
      }),
      transformResponse: (response: PaginatedRequestResponse) => response,
      providesTags: ["CommunityRequests"],
    }),

    updateRequestStatus: builder.mutation<
      ApiResponse<void>,
      {
        community_id: string;
        user_id: string;
        status: string;
        decline_reason?: string;
        role?: string;
      }
    >({
      query: ({ community_id, user_id, status, decline_reason, role }) => ({
        url: `communities/requests/${community_id}/${user_id}`,
        method: "PATCH",
        data: { status, decline_reason, role },
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["CommunityRequests"],
    }),

    // Event Management APIs
    getCommunityEvents: builder.query<
      {
        status: boolean;
        message: string;
        data: IEventData[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      },
      IEventQueryParams
    >({
      query: ({ community_id, ...params }) => ({
        url: `communities/${community_id}/events`,
        method: "GET",
        params: {
          page: params.page,
          limit: params.limit,
          status: params.status,
          search: params.search,
        },
      }),
      transformResponse: (response: {
        status: boolean;
        message: string;
        data: IEventData[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }) => response,
      providesTags: ["Events"],
      keepUnusedDataFor: 0,
    }),

    getEventById: builder.query<
      IEventData,
      { communityId: string; eventId: string }
    >({
      query: ({ communityId, eventId }) => ({
        url: `communities/${communityId}/events/${eventId}`,
        method: "GET",
      }),
      transformResponse: (response: { status: boolean; data: IEventData }) =>
        response.data,
      providesTags: (result, error, { eventId }) => [
        { type: "Events", id: eventId },
      ],
      keepUnusedDataFor: 0,
    }),

    createEvent: builder.mutation<
      ApiResponse<IEventData>,
      {
        communityId: string;
        data: FormData;
      }
    >({
      query: ({ communityId, data }) => ({
        url: `communities/${communityId}/events`,
        method: "POST",
        data,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
      transformResponse: (response: ApiResponse<IEventData>) => response,
      invalidatesTags: ["Events"],
    }),

    updateEvent: builder.mutation<
      ApiResponse<IEventData>,
      {
        communityId: string;
        eventId: string;
        data: FormData | Partial<IEventData>;
      }
    >({
      query: ({ communityId, eventId, data }) => ({
        url: `communities/${communityId}/events/${eventId}`,
        method: "PATCH",
        data,
        headers:
          data instanceof FormData
            ? {
                "Content-Type": "multipart/form-data",
              }
            : {},
      }),
      transformResponse: (response: ApiResponse<IEventData>) => response,
      invalidatesTags: ["Events"],
    }),

    deleteEvent: builder.mutation<
      ApiResponse<void>,
      { communityId: string; eventId: string }
    >({
      query: ({ communityId, eventId }) => ({
        url: `communities/${communityId}/events/${eventId}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Events"],
    }),

    // Translation API for Communities (temporary, reusing POI endpoint)
    translateCommunityContent: builder.mutation<
      {
        translations: Record<string, ICommunityTranslation>;
        message?: string;
        warning_message?: string;
      },
      {
        description?: string;
        tags?: string[];
        source_language?: string;
        community_id?: string;
      }
    >({
      query: (data) => ({
        url: "communities/translate",
        method: "POST",
        data,
      }),
      transformResponse: (
        response:
          | {
              status?: boolean;
              message?: string;
              warning_message?: string;
              translations?: Record<string, ICommunityTranslation> | null;
              data?: Record<string, ICommunityTranslation> | null;
            }
          | Record<string, ICommunityTranslation>,
      ) => {
        type TranslationMap = Record<string, ICommunityTranslation>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const maybeWrapper = response as any;

        let languageMap: TranslationMap | undefined = undefined;
        let message: string | undefined = undefined;
        let warning_message: string | undefined = undefined;

        if (maybeWrapper && typeof maybeWrapper === "object") {
          message = maybeWrapper.message || maybeWrapper.data?.message;
          warning_message = maybeWrapper.warning_message || maybeWrapper.data?.warning_message;
          
          if ("translations" in maybeWrapper && maybeWrapper.translations) {
            languageMap = maybeWrapper.translations || ({} as TranslationMap);
          } else if ("data" in maybeWrapper && maybeWrapper.data) {
            // Also handle if translations are nested inside data.translations
            languageMap = maybeWrapper.data.translations || maybeWrapper.data || ({} as TranslationMap);
          }
        }

        if (!languageMap) {
          languageMap = (response as TranslationMap) || ({} as TranslationMap);
        }

        return { 
          translations: languageMap,
          message,
          warning_message
        };
      },
    }),

    // Translation API for Events
    translateEventContent: builder.mutation<
      {
        translations: Record<
          string,
          {
            title?: string;
            description?: string;
            location_text?: string;
          }
        >;
        message?: string;
        warning_message?: string;
      },
      {
        title?: string;
        description?: string;
        location_text?: string;
        source_language: string;
        event_id?: string;
      }
    >({
      query: (data) => ({
        url: "communities/events/translate",
        method: "POST",
        data,
      }),
      transformResponse: (
        response:
          | {
              status?: boolean;
              message?: string;
              warning_message?: string;
              data?: {
                [key: string]: {
                  title?: string;
                  description?: string;
                  location_text?: string;
                };
              } | null;
            }
          | {
              [key: string]: {
                title?: string;
                description?: string;
                location_text?: string;
              };
            },
      ) => {
        type TranslationMap = Record<
          string,
          {
            title?: string;
            description?: string;
            location_text?: string;
          }
        >;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const maybeWrapper = response as any;

        let message: string | undefined = undefined;
        let warning_message: string | undefined = undefined;
        
        if (maybeWrapper && typeof maybeWrapper === "object") {
          message = maybeWrapper.message || maybeWrapper.data?.message;
          warning_message = maybeWrapper.warning_message || maybeWrapper.data?.warning_message;
        }

        const languageMap: TranslationMap =
          maybeWrapper &&
          typeof maybeWrapper === "object" &&
          "data" in maybeWrapper
            ? maybeWrapper.data?.translations || maybeWrapper.data || ({} as TranslationMap)
            : (response as TranslationMap) || ({} as TranslationMap);

        return {
          translations: languageMap,
          message,
          warning_message,
        };
      },
    }),

    // Get Event RSVP List
    getEventRsvpList: builder.query<
      {
        status: boolean;
        message: string;
        data: Array<{
          name: string;
          email: string;
          rsvp_created_at: string;
        }>;
      },
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `communities/events/${eventId}/rsvps`,
        method: "GET",
      }),
      transformResponse: (response: {
        status: boolean;
        message: string;
        data: Array<{
          name: string;
          email: string;
          rsvp_created_at: string;
        }>;
      }) => response,
      providesTags: (result, error, { eventId }) => [
        { type: "Events", id: `${eventId}-rsvp` },
      ],
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetCommunitiesQuery,
  useGetCommunityByIdQuery,
  useCreateCommunityMutation,
  useUpdateCommunityMutation,
  useDeleteCommunityMutation,
  useUpdateCommunityStatusMutation,
  useGetCommunityRequestsQuery,
  useUpdateRequestStatusMutation,
  useGetCommunityEventsQuery,
  useGetEventByIdQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useTranslateEventContentMutation,
  useGetEventRsvpListQuery,
  useTranslateCommunityContentMutation,
} = CommunityManagementService;
